import React, { useEffect, useState } from "react";
import ExaminationSidebar from "../../components/sidebar/ExaminationSidebar";
import ExaminationNavbar from "../../components/navbar/ExaminationNavbar.jsx";
import "./adminOverview.scss";
import Widget from "../../components/widget/Widget";
import LineCharts from "../../components/chart/LineChart";
import ExaminationCard from "../../components/card/ExaminationCard.jsx";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import StackedBarChart from "../../components/chart/StackedBarChart.jsx";
import Select from "react-select";
import { serverUrl } from "../../temp.js";
import { monthNames } from "../../enum.js";

const AdminOverview = () => {
  const centerId = localStorage.getItem("center_id") || 38;
  const [token] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(
    localStorage.getItem("sidebar") || false
  );
  const [selectedClassId, setSelectedClassId] = useState(() => {
    const stored = localStorage.getItem("class-id");
    return stored ? stored : "7"; // Default to class ID 7 if nothing stored
  });

  // Dropdown states
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedInvigilators, setSelectedInvigilators] = useState([]);

  // Options states
  const [monthOptions, setMonthOptions] = useState([]);
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [studentOptions, setStudentOptions] = useState([]);

  // Chart data states
  const [teacherAttendanceData, setTeacherAttendanceData] = useState([]);
  const [studentAttendanceData, setStudentAttendanceData] = useState([]);

  // Add state for Invigilator attendance
  const [invigilatorOptions, setInvigilatorOptions] = useState([]);
  const [invigilatorAttendanceData, setInvigilatorAttendanceData] = useState(
    []
  );
  const [loadingAttendance, setLoadingAttendance] = useState({
    teachers: false,
    students: false,
    invigilators: false,
  });

  const idealGrades = ["A1", "A", "B", "C", "D", "Retry"];
  const idealColors = [
    "#00C49F",
    "#0088FE",
    "#FFBB28",
    "#FF8042",
    "#AF19FF",
    "#FF193D",
  ];

  // Loading states
  const [loadingSubjectBarChart, setLoadingSubjectBarChart] = useState(false);
  const [loading, setLoading] = useState({
    months: true,
    subjects: true,
    teachers: true,
    students: true,
    lineChart: true,
    pieChart: true,
    stackedBarChart: true,
    cards: true,
  });

  const redirectToLogin = () => {
    alert("Please Login first then you can access this page...");
    window.location.href = "/";
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
    localStorage.setItem("sidebar", !sidebarOpen);
  };

  const handleClassChange = (newClassId) => {
    setSelectedClassId(newClassId);
    localStorage.setItem("class-id", newClassId);

    // Reset filters when class changes
    setSelectedMonths([]);
    setSelectedTeachers([]);
    setSelectedStudents([]);
  };

  // Fetch month options
  useEffect(() => {
    fetch(
      `${serverUrl}/result_dashboard/month_dropdown?centerId=${centerId}&classId=${selectedClassId}`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.code === "200") {
          setMonthOptions(
            data.data.map((month) => ({
              value: month.month,
              label: month.month_name,
            }))
          );
        }
        setLoading((prev) => ({ ...prev, months: false }));
      })
      .catch((error) => {
        console.error("Error fetching months:", error);
        setLoading((prev) => ({ ...prev, months: false }));
      });
  }, [selectedClassId]);

  // Fetch teacher options when subjects change
  useEffect(() => {
    if (!selectedClassId) {
      setTeacherOptions([]);
      setSelectedTeachers([]);
      return;
    }

    setLoading((prev) => ({ ...prev, teachers: true }));
    const subjectIds = selectedSubjects.map((sub) => sub.value);

    fetch(
      `${serverUrl}/result_dashboard/teacher_dropdown?centerId=${centerId}&classId=${selectedClassId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ subjectId: [] }),
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.code === "200") {
          setTeacherOptions(
            data.data.map((teacher) => ({
              value: teacher.id,
              label: teacher.teacher_name,
            }))
          );
        }
        setLoading((prev) => ({ ...prev, teachers: false }));
      })
      .catch((error) => {
        console.error("Error fetching teachers:", error);
        setLoading((prev) => ({ ...prev, teachers: false }));
      });
  }, [selectedClassId]);

  // Fetch student options when class changes
  useEffect(() => {
    if (!selectedClassId) return;

    setLoading((prev) => ({ ...prev, students: true }));
    fetch(
      `${serverUrl}/result_dashboard/student_dropdown?centerId=${centerId}&classId=${selectedClassId}`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.code === "200") {
          setStudentOptions(
            data.data.map((student) => ({
              value: student.id,
              label: student.name,
            }))
          );
        }
        setLoading((prev) => ({ ...prev, students: false }));
      })
      .catch((error) => {
        console.error("Error fetching students:", error);
        setLoading((prev) => ({ ...prev, students: false }));
      });
  }, [selectedClassId]);

  // Fetch Invigilator dropdown on mount
  useEffect(() => {
    fetch(`${serverUrl}/result_dashboard/invigilators?center_id=${centerId}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.code === "200") {
          setInvigilatorOptions(
            data.data.map((inv) => ({
              value: inv.user_id.toString(),
              label: inv.name,
            }))
          );
        }
      })
      .catch((err) => console.error("Error fetching invigilators:", err));
  }, [centerId]);

  // Fetch teacher attendance data
  const fetchTeacherAttendance = () => {
    setLoadingAttendance((prev) => ({ ...prev, teachers: true }));
    const teacherIds = selectedTeachers.map((t) => t.value);
    const months = selectedMonths.map((m) => m.value);

    fetch(
      `${serverUrl}/result_dashboard/teacher-attendance-monthly?centerId=${centerId}&classId=${selectedClassId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          months: months,
          teacherids: teacherIds,
        }),
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.code === "200") {
          setTeacherAttendanceData(data.data);
        }
        setLoadingAttendance((prev) => ({ ...prev, teachers: false }));
      })
      .catch((error) => {
        console.error("Error fetching teacher attendance:", error);
        setLoadingAttendance((prev) => ({ ...prev, teachers: false }));
      });
  };

  // Fetch student attendance data
  const fetchStudentAttendance = () => {
    setLoadingAttendance((prev) => ({ ...prev, students: true }));
    const studentIds = selectedStudents.map((s) => s.value);
    const months = selectedMonths.map((m) => m.value);

    fetch(
      `${serverUrl}/result_dashboard/student-attendance-monthly?centerId=${centerId}&classId=${selectedClassId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          months: months,
          studentids: studentIds,
        }),
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.code === "200") {
          setStudentAttendanceData(data.data);
        }
        setLoadingAttendance((prev) => ({ ...prev, students: false }));
      })
      .catch((error) => {
        console.error("Error fetching student attendance:", error);
        setLoadingAttendance((prev) => ({ ...prev, students: false }));
      });
  };

  // Fetch Invigilator attendance
  const fetchInvigilatorAttendance = () => {
    setLoadingAttendance((prev) => ({ ...prev, invigilators: true }));
    const invigilatorIds = selectedInvigilators.map((i) => i.value) || [];
    const months = selectedMonths.map((m) => m.value) || [];

    fetch(
      `${serverUrl}/result_dashboard/invigilator-attendance?centerId=${centerId}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          monthId: months,
          invigilatorId: invigilatorIds,
        }),
      }
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.code === "200") {
          setInvigilatorAttendanceData(data.data);
        }
        setLoadingAttendance((prev) => ({ ...prev, invigilators: false }));
      })
      .catch((err) => {
        console.error("Error fetching invigilator attendance:", err);
        setLoadingAttendance((prev) => ({ ...prev, invigilators: false }));
      });
  };

  // Transform attendance data for chart display
  // const transformAttendanceData = (data, type) => {
  //     // Get all unique months from the data
  //     const monthSet = new Set();
  //     data.forEach(item => {
  //         Object.keys(item.attendanceByMonth).forEach(month => {
  //             monthSet.add(month);
  //         });
  //     });
  //     const months = Array.from(monthSet).sort();

  //     // Create chart data structure
  //     const chartData = data.map(item => {
  //         let dataItem;
  //         if(type === 'student'){
  //             dataItem = {
  //                 id: item[`${type}Id`],
  //                 name: item[`commonName`]
  //             };
  //         }else{
  //             dataItem = {
  //                 id: item[`${type}Id`],
  //                 name: item[`${type}Name`]
  //             };
  //         }

  //         months.forEach(month => {
  //             dataItem[month] = item.attendanceByMonth[month] || 0;
  //         });

  //         return dataItem;
  //     });

  //     return { chartData, months };
  // };

  const transformAttendanceData = (data, type) => {
    const monthSet = new Set();
    data.forEach((item) => {
      Object.keys(item.attendanceByMonth).forEach((month) => {
        monthSet.add(month); // e.g., "2025-06"
      });
    });

    const monthsRaw = Array.from(monthSet).sort(); // Keep chronological order
    const monthsFormatted = monthsRaw.map((m) => {
      const [year, month] = m.split("-");
      return `${monthNames[parseInt(month, 10) - 1]} ${year}`;
    });

    const chartData = data.map((item) => {
      let dataItem;
      if (type === "student") {
        dataItem = { id: item[`${type}Id`], name: item[`commonName`] };
      } else {
        dataItem = { id: item[`${type}Id`], name: item[`${type}Name`] };
      }

      monthsRaw.forEach((month, idx) => {
        const label = monthsFormatted[idx];
        dataItem[label] = item.attendanceByMonth[month] || 0;
      });

      return dataItem;
    });

    return { chartData, months: monthsFormatted };
  };

  // Format applied filters
  const formatAppliedFilters = (filters) => {
    return filters
      .filter((filter) => filter.value.length > 0)
      .map((filter) => ({
        label: filter.label,
        value: filter.value.map((v) => v.label).join(", "),
      }));
  };

  // Teacher attendance filters
  const teacherAttendanceFilters = formatAppliedFilters([
    { label: "Month", value: selectedMonths },
    { label: "Teacher", value: selectedTeachers },
  ]);

  // Student attendance filters
  const studentAttendanceFilters = formatAppliedFilters([
    { label: "Month", value: selectedMonths },
    { label: "Student", value: selectedStudents },
  ]);

  // Fetch data when filters change
  useEffect(() => {
    if (selectedClassId) {
      fetchTeacherAttendance();
      fetchStudentAttendance();
    }
  }, [selectedClassId, selectedMonths, selectedTeachers, selectedStudents]);

  // Transform data for charts
  const { chartData: teacherChartData, months: teacherMonths } =
    transformAttendanceData(teacherAttendanceData, "teacher");
  const { chartData: studentChartData, months: studentMonths } =
    transformAttendanceData(studentAttendanceData, "student");

  // Transform attendance for chart
  const { chartData: invigilatorChartData, months: invigilatorMonths } =
    transformAttendanceData(invigilatorAttendanceData, "invigilator");

  // Applied filters for chart tooltip
  const invigilatorAttendanceFilters = formatAppliedFilters([
    { label: "Month", value: selectedMonths },
    { label: "Invigilator", value: selectedInvigilators },
  ]);

  // Trigger fetching when filters change
  useEffect(() => {
    fetchInvigilatorAttendance();
  }, [selectedMonths, selectedInvigilators]);

  return (
    <>
      {!token && redirectToLogin()}
      {token && (
        <div className="adminOverview">
          {sidebarOpen && (
            <ExaminationSidebar isOpen={sidebarOpen} toggle={toggleSidebar} />
          )}
          <div className="homeContainer">
            <ExaminationNavbar
              isOpen={sidebarOpen}
              toggle={toggleSidebar}
              selectedClassId={selectedClassId}
              onClassChange={handleClassChange}
            />
            <div className="heading">
              <h2>Admin Overview</h2>
            </div>
            <div className="widgets">
              <div className="dropdown-container">
                <label htmlFor="dropdown1">Month</label>
                <Select
                  id="dropdown1"
                  isMulti
                  options={monthOptions}
                  value={selectedMonths}
                  onChange={setSelectedMonths}
                  isLoading={loading.months}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
              <div className="dropdown-container">
                <label htmlFor="dropdown4">Student</label>
                <Select
                  id="dropdown4"
                  isMulti
                  options={studentOptions}
                  value={selectedStudents}
                  onChange={setSelectedStudents}
                  isLoading={loading.students}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
              <div className="dropdown-container">
                <label htmlFor="dropdown3">Teacher</label>
                <Select
                  id="dropdown3"
                  isMulti
                  options={teacherOptions}
                  value={selectedTeachers}
                  onChange={setSelectedTeachers}
                  isLoading={loading.teachers}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
              {/* <div className="dropdown-container">
                                <label htmlFor="dropdown4">Invigilator</label>
                                <Select
                                    id="dropdown4"
                                    isMulti
                                    options={invigilatorOptions}
                                    value={selectedInvigilators}
                                    onChange={setSelectedInvigilators}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />
                            </div> */}
              <div className="dropdown-container">
                <label htmlFor="dropdownInvigilator">Invigilator</label>
                <Select
                  id="dropdownInvigilator"
                  isMulti
                  options={invigilatorOptions}
                  value={selectedInvigilators}
                  onChange={setSelectedInvigilators}
                  isLoading={loadingAttendance.invigilators}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
            </div>

            <div className="charts">
              <StackedBarChart
                title={"Teacher Attendance"}
                data={teacherChartData}
                subjectKeys={teacherMonths}
                loading={loadingAttendance.teachers}
                appliedFilters={teacherAttendanceFilters}
              />
              {/* Placeholder for Invigilator Attendance */}
              {/* <div className="stackedBarChartContainer">
                                <div className="chart-header">
                                    <h2 className="chartTitle">Invigilator Attendance</h2>
                                </div>
                                <div className="no-data">No data available</div>
                            </div> */}
            </div>

            <div className="charts">
              <StackedBarChart
                title={"Student Attendance"}
                data={studentChartData}
                subjectKeys={studentMonths}
                loading={loadingAttendance.students}
                appliedFilters={studentAttendanceFilters}
              />
            </div>
            {/* <div className="charts">
                            <div className="stackedBarChartContainer">
                                <div className="chart-header">
                                    <h2 className="chartTitle">Invigilator Attendance</h2>
                                </div>
                                <div className="no-data">No data available</div>
                            </div>
                        </div> */}
            <div className="charts">
              <StackedBarChart
                title={"Invigilator Attendance"}
                data={invigilatorChartData}
                subjectKeys={invigilatorMonths}
                loading={loadingAttendance.invigilators}
                appliedFilters={invigilatorAttendanceFilters}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminOverview;
